import { Modal } from '@repo/ui';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { getHasModalOpened, setModalOpened } from 'store/modal';

import { FirstLookCarousel } from './FirstLookCarousel';

import { ActionPlan } from './slides/ActionPlan';
import { ActionPlanDietary } from './slides/ActionPlanDietary';
import { ActionPlanLifestyle } from './slides/ActionPlanLifestyle';
import { ActionPlanSupplements } from './slides/ActionPlanSupplements';
import { Conditions } from './slides/Conditions';
import { ConditionsIntro } from './slides/ConditionsIntro';
import { ConditionSpotlight } from './slides/ConditionSpotlight';
import {
  CONDITIONS_INTRO_SLIDE_ID,
  conditionSpotlightSlideId,
  selectMarkedConditionSpotlights,
} from './slides/conditionsSlides';
import { DontWorry } from './slides/DontWorry';
import { End } from './slides/End';
import { Intro } from './slides/Intro';
import { MicrobiomeBreakdown } from './slides/MicrobiomeBreakdown';
import { MicrobiomeType } from './slides/MicrobiomeType';
import { Mss } from './slides/Mss';
import { Overview } from './slides/Overview';
import { TopThreeIntro } from './slides/TopThreeIntro';
import { TopThreeItem } from './slides/TopThreeItem';

import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import useGutBodyProps from 'hooks/useGutBodyProps';
import { useProcessedActionPlanData } from 'views/Results/Summary/FirstLook/hooks/useProcessedActionPlanData';
import { SLIDE_BACKGROUND_COLORS } from './slideBackgroundColors';

import { KitType } from '@repo/types';
import { AgeGroup, getAgeGroup, getKitSciVersion } from '@repo/utils';
import checkIsKitPro from '@repo/utils/checkIsKitPro';

import useFeature from 'services/features/useFeature';
import useRetestInfo from 'services/mss/useRetestInfo';
import { useOrgansNonSuspense } from 'services/organs/useOrgans';
import { useMainFindingsWithRanges } from '../SummaryCards/NoteCard/useMainFindings';
import styles from './FirstLookPopup.module.css';
import { Maturation } from './slides/Maturation';
import MicrobiomeHealthScores from './slides/MicrobiomeHealthScores';
import { ProMss } from './slides/ProMss';
import { RetestPrompt } from './slides/RetestPrompt';

const FirstLookPopup = () => {
  const { currentKit } = useCurrentKitAndTinyAccount();
  const { age, pregnant, sex } = useGutBodyProps();
  const ageGroup = currentKit?.age_at_sampling
    ? getAgeGroup(currentKit.age_at_sampling)
    : null;
  const isBaby = ageGroup === AgeGroup.BABY;

  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();

  const modalIdentifier = {
    name: 'firstLook',
    props: {},
  };

  const isPopupDismissed = useSelector(getHasModalOpened(modalIdentifier));
  const sciVersion = getKitSciVersion(currentKit);
  const { data: mainFindingsData } = useMainFindingsWithRanges(
    currentKit?.id,
    sciVersion,
  );
  const topThreeMetrics = mainFindingsData?.mainFindings || [];
  const actionPlanData = useProcessedActionPlanData();

  const { resampleMonth } = useRetestInfo(currentKit?.id);
  const { isFeatureEnabled: isFirstLookV3Enabled } = useFeature(
    'first_look_popup_v3',
  );

  const { isFeatureEnabled: isConditionsSlidesEnabled } = useFeature(
    'new_conditions_body_component_slides',
  );
  const showConditionsSlides =
    currentKit?.sampling_type === KitType.STOOL && isConditionsSlidesEnabled;

  // Non-suspense on purpose: the popup renders its slides eagerly and shares the
  // organs cache with GutBodyConnectionsCard, so this rarely fires a request of its own.
  const { organs: conditionOrgans } = useOrgansNonSuspense(
    showConditionsSlides ? currentKit?.id : undefined,
  );

  const isVisible = !isPopupDismissed;

  // The action plan slide numbers the retest as its final step, so both
  // surfaces have to agree on whether the retest slide is present at all.
  const showRetestSlide = Boolean(resampleMonth) && isFirstLookV3Enabled;

  const isProKit = checkIsKitPro(currentKit);
  const hasTopThreeMetrics = topThreeMetrics.length > 0;
  const hasActionPlanSteps = actionPlanData.steps.length > 0;
  const hasDietaryRecommendations = actionPlanData.dietaryTotal > 0;
  const hasLifestyleRecommendations = actionPlanData.lifestyleTotal > 0;
  const hasSupplementRecommendations = actionPlanData.supplementsTotal > 0;

  const dismissPopup = () => {
    dispatch(setModalOpened(modalIdentifier));
  };

  React.useEffect(() => {
    if (isVisible) {
      trackEvent('First Look', {
        event_type: 'modal_opened',
        total_slides: slides.length,
      });
    }
  }, []);

  if (!isVisible) {
    return null;
  }

  const conditionSpotlightSlides = showConditionsSlides
    ? selectMarkedConditionSpotlights(conditionOrgans, {
        age,
        pregnant,
        sex,
      }).map(({ organ, condition }) => ({
        id: conditionSpotlightSlideId(condition),
        component: <ConditionSpotlight organ={organ} condition={condition} />,
        backgroundColor: SLIDE_BACKGROUND_COLORS.conditionSpotlight,
        tapAreaHeight: 'calc(100% - 80px)',
      }))
    : [];

  const slides = [
    {
      id: 'intro',
      component: <Intro />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.intro,
    },
    {
      id: 'overview',
      component: <Overview />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.overview,
    },
    {
      id: 'mss',
      component: <Mss />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.mss,
    },
    ...(isProKit
      ? [
          {
            id: 'pro-mss',
            component: <ProMss />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.mss,
          },
        ]
      : []),
    {
      id: 'microbiome-type',
      component: <MicrobiomeType />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.gutType,
    },
    {
      id: 'microbiome-breakdown',
      component: <MicrobiomeBreakdown />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.microbiomeBreakdown,
    },
    {
      id: 'microbiome-health-scores',
      component: <MicrobiomeHealthScores mainFindings={topThreeMetrics} />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.microbiomeHealthScores,
    },
    ...(showConditionsSlides
      ? [
          {
            id: CONDITIONS_INTRO_SLIDE_ID,
            component: <ConditionsIntro />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.conditionsIntro,
            tapAreaHeight: 'calc(100% - 80px)',
          },
          ...conditionSpotlightSlides,
        ]
      : []),
    ...(ageGroup &&
    (ageGroup === AgeGroup.BABY || ageGroup === AgeGroup.TODDLER)
      ? [
          {
            id: 'maturation-index',
            component: <Maturation />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.microbiomeCategories,
          },
        ]
      : []),
    ...(hasTopThreeMetrics
      ? [
          {
            id: 'top-three-intro',
            component: <TopThreeIntro count={topThreeMetrics.length} />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.topThreeIntro,
          },
          ...topThreeMetrics.map((metric, index) => ({
            id: `top-three-${metric.gvh?.name || index}`,
            component: (
              <TopThreeItem
                metric={metric}
                count={topThreeMetrics.length}
                priorityNumber={index + 1}
              />
            ),
            backgroundColor: SLIDE_BACKGROUND_COLORS.topThreeItem,
            tapAreaHeight: 'calc(100% - 80px)',
          })),
        ]
      : []),
    ...(!showConditionsSlides
      ? [
          {
            id: 'conditions',
            component: <Conditions />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.conditions,
            tapAreaHeight: 'calc(100% - 80px)',
          },
        ]
      : []),
    ...(!isBaby && hasActionPlanSteps
      ? [
          {
            id: 'dont-worry',
            component: <DontWorry />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.dontWorry,
          },
        ]
      : []),
    ...(hasActionPlanSteps
      ? [
          {
            id: 'action-plan',
            component: <ActionPlan showRetestStep={showRetestSlide} />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.actionPlan,
          },
        ]
      : []),
    ...(hasDietaryRecommendations
      ? [
          {
            id: 'action-plan-dietary',
            component: <ActionPlanDietary />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.actionPlanDietary,
          },
        ]
      : []),
    ...(hasLifestyleRecommendations && hasActionPlanSteps
      ? [
          {
            id: 'action-plan-lifestyle',
            component: <ActionPlanLifestyle />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.actionPlanLifestyle,
          },
        ]
      : []),
    ...(hasSupplementRecommendations && hasActionPlanSteps
      ? [
          {
            id: 'action-plan-supplements',
            component: <ActionPlanSupplements />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.actionPlanSupplements,
          },
        ]
      : []),
    ...(showRetestSlide && resampleMonth
      ? [
          {
            id: 'retest-prompt',
            component: (
              <RetestPrompt
                resampleMonth={resampleMonth}
                topThreeMetrics={topThreeMetrics}
              />
            ),
            backgroundColor: SLIDE_BACKGROUND_COLORS.retestPrompt,
            duration: 15000,
          },
        ]
      : []),
    {
      id: 'end',
      component: <End />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.end,
      tapAreaHeight: 'calc(100% - 140px)',
    },
  ];

  return (
    <Modal
      isOpen={true}
      className={styles.container}
      onClose={dismissPopup}
      showFloatingX={false}
      hasBodyPadding={false}
      closeOnBackdropClick={false}
    >
      <FirstLookCarousel slides={slides} onClose={dismissPopup} />
    </Modal>
  );
};

export default FirstLookPopup;
